/**
 * Yale Bright Star Catalogue reader.
 *
 * The asset is baked by `tools/atlas/bake_stars.py`; its provenance, licence and
 * byte layout live beside it in `public/art/atlas/stars/bsc5.json`. Directions
 * are unit vectors in the ecliptic J2000 frame, the same frame the world model
 * propagates orbits in, so nothing here converts anything.
 *
 * No three.js and no DOM types beyond `fetch`, so a Node ruler can import it.
 */

export const STARS_URL = '/art/atlas/stars/bsc5.bin';

/** Bytes per record: x, y, z, visual magnitude, B-V, all float32 little-endian. */
export const STAR_RECORD_BYTES = 20;

/**
 * Traditional naked-eye limit, used below as the reference point for point size
 * and intensity - not the catalogue's actual faint end. `bake_stars.py` only
 * drops rows with no coordinates or no magnitude, so the baked catalogue keeps
 * everything V/50 has, out to magnitude 7.96.
 */
export const STAR_MAG_LIMIT = 6.5;

export interface StarCatalogue {
  count: number;
  /** Unit vectors in ecliptic J2000, three floats per star. */
  dir: Float32Array;
  /** Visual magnitude, ascending (brightest first). */
  mag: Float32Array;
  /** B-V colour index, NaN where the catalogue has none. */
  bv: Float32Array;
}

export function parseStars(buffer: ArrayBuffer): StarCatalogue {
  if (buffer.byteLength === 0 || buffer.byteLength % STAR_RECORD_BYTES !== 0) {
    throw new Error(`star catalogue is ${buffer.byteLength} bytes, `
      + `not a whole number of ${STAR_RECORD_BYTES}-byte records`);
  }
  const count = buffer.byteLength / STAR_RECORD_BYTES;
  const dir = new Float32Array(count * 3);
  const mag = new Float32Array(count);
  const bv = new Float32Array(count);

  // DataView, not a Float32Array over the buffer: the file is little-endian by
  // definition and this reads it that way on any host.
  const view = new DataView(buffer);
  for (let i = 0; i < count; i++) {
    const o = i * STAR_RECORD_BYTES;
    dir[i * 3] = view.getFloat32(o, true);
    dir[i * 3 + 1] = view.getFloat32(o + 4, true);
    dir[i * 3 + 2] = view.getFloat32(o + 8, true);
    mag[i] = view.getFloat32(o + 12, true);
    bv[i] = view.getFloat32(o + 16, true);
  }
  return { count, dir, mag, bv };
}

export async function loadStars(url: string = STARS_URL): Promise<StarCatalogue> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`star catalogue ${url}: HTTP ${res.status}`);
  return parseStars(await res.arrayBuffer());
}

/**
 * B-V colour index to linear RGB, normalised so the brightest channel is 1.
 *
 * B-V to effective temperature is Ballesteros (2012); temperature to chromaticity
 * is the Planckian-locus approximation of Kim et al. (2002); chromaticity to
 * linear sRGB is the plain D65 matrix. Chromaticity only - magnitude carries the
 * intensity, see `starIntensity`.
 */
export function starColor(bv: number): [number, number, number] {
  const t = starTemperature(bv);
  const [x, y] = planckianChromaticity(t);
  if (y <= 0) return [1, 1, 1];

  const bigX = x / y;
  const bigY = 1;
  const bigZ = (1 - x - y) / y;

  let r = 3.2406 * bigX - 1.5372 * bigY - 0.4986 * bigZ;
  let g = -0.9689 * bigX + 1.8758 * bigY + 0.0415 * bigZ;
  let b = 0.0557 * bigX - 0.204 * bigY + 1.057 * bigZ;
  r = Math.max(0, r);
  g = Math.max(0, g);
  b = Math.max(0, b);

  const peak = Math.max(r, g, b);
  if (peak <= 0) return [1, 1, 1];
  return [r / peak, g / peak, b / peak];
}

/** Effective temperature in kelvin for a B-V colour index. NaN B-V reads as the Sun. */
export function starTemperature(bv: number): number {
  const c = Number.isFinite(bv) ? Math.min(2.0, Math.max(-0.4, bv)) : 0.65;
  const t = 4600 * (1 / (0.92 * c + 1.7) + 1 / (0.92 * c + 0.62));
  return Math.min(25000, Math.max(1667, t));
}

function planckianChromaticity(t: number): [number, number] {
  const k = 1000 / t;
  const x = t < 4000
    ? -0.2661239 * k * k * k - 0.234358 * k * k + 0.8776956 * k + 0.17991
    : -3.0258469 * k * k * k + 2.1070379 * k * k + 0.2226347 * k + 0.24039;
  let y: number;
  if (t < 2222) {
    y = -1.1063814 * x * x * x - 1.3481102 * x * x + 2.18555832 * x - 0.20219683;
  } else if (t < 4000) {
    y = -0.9549476 * x * x * x - 1.37418593 * x * x + 2.09137015 * x - 0.16748867;
  } else {
    y = 3.081758 * x * x * x - 5.8733867 * x * x + 3.75112997 * x - 0.37001483;
  }
  return [x, y];
}

/**
 * Point diameter in pixels at device pixel ratio 1.
 *
 * Apparent size has to grow far more slowly than flux does: a linear ramp in
 * magnitude reads as a sky, while anything near the real 1500:1 flux range over
 * the catalogue's eight magnitudes reads as a handful of blobs on black.
 */
export function starPointSize(mag: number): number {
  const s = 0.85 + (STAR_MAG_LIMIT - mag) * 0.36;
  return Math.min(4.2, Math.max(0.7, s));
}

/**
 * Relative intensity multiplier, 0 to 1, for the additive pass.
 *
 * Flux is 10^(-0.4 dm), so the catalogue spans 1500:1 and everything brighter
 * than fourth magnitude would clip. The exponent is compressed to 0.15 to keep
 * the ordering visible across the whole range, and the faint end starts high
 * enough to read over the sim's nebula, which is brighter than any real sky
 * background; that costs saturation on the twenty or so stars above magnitude 2.
 */
export function starIntensity(mag: number): number {
  return Math.min(1, 0.16 * Math.pow(10, -0.15 * (mag - STAR_MAG_LIMIT)));
}
